import { Badge, Table } from 'antd';
import moment from 'moment';
import React, { FC, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IAdditionalParams, IPayment } from '../../../../interfaces';
import { useCurrentTabShownCurrency, workspaceContext } from '../../../../state';
import IconReceipt from '../../../../ui/icons/IconReceipt';
import TooltipCustom from '../../../../ui/tooltip-custom';
import { TooltipPlacement } from '../../../../ui/tooltip-custom/interfaces';
import { openUrl, openWebsitePricing } from '../../../../utils/open-site';
import { PLAN_IDS } from '../../../pricing/constants';
import { E_CURRENCY } from '../../../pricing/interfaces';
import { Price } from '../../../pricing/utils';
import { ReceiptIconContainerWithDescription } from '../../styles';
import { ButtonPay, ContainerNameCol, Row, TablePan, TextTable } from '../domElements/billing';
import { submitCreateInvoice } from '../../api';

interface IPaymentsTable {
  payments: IPayment[];
  isQuickSettingsEnabled?: boolean;
}

const isElectron = !!window.require;

const PaymentsTable: FC<IPaymentsTable> = (props) => {
  const { payments, isQuickSettingsEnabled } = props;

  const [anchorElTooltip, setAnchorElTooltip] = useState<HTMLElement | null>(null);
  const [tooltipPlacement, setTooltipPlacement] = useState<TooltipPlacement>('top');
  const [tooltipText, setTooltipText] = useState<string | null>('');

  const isElectron = !!window.require;

  const localCurrency = useCurrentTabShownCurrency();

  const { id: workspaceId } = useContext(workspaceContext);

  const { t: translation } = useTranslation();

  const formatDate = (date: string): string => moment(date).utc().format('MMM DD, YYYY');

  const renderDate = (date: string): JSX.Element => (
    <TextTable>
      {formatDate(date)}
    </TextTable>
  );

  const renderExpiresDate = (date: string): JSX.Element => (
    <TextTable>
      {formatDate(date)}
    </TextTable>
  );

  const renderPaymentMethod = (description = '', payment: IPayment): JSX.Element => {
    let method: 'Stripe' | 'PayPal' | 'BTC' | 'Trial' | 'Forever Free' = 'Stripe';
    if (description.toLowerCase().includes('paypal') || payment.additionalParams?.description === 'Paypal') {
      method = 'PayPal';
    }

    if (description.toLowerCase().includes('crypto')) {
      method = 'BTC';
    }

    if (payment.isTrial) {
      method = 'Trial';
    }

    if (payment.plan === PLAN_IDS.ForeverFree) {
      method = 'Forever Free';
    }

    return (
      <TextTable>
        {method === 'Stripe' ? `Credit Card ${payment.creditCard}` : method}
      </TextTable>
    );
  };

  const renderStatus = (status = ''): JSX.Element => {
    const statusBadge = status === 'success' ? 'success' : 'default';
    const textBadge = status === 'success' ? translation('base.paid') : translation('base.unpaid');

    return (
      <Row>
        <Badge
          dot={true}
          status={statusBadge}
          text={textBadge}
        />
        <TextTable style={{ marginLeft: 5 }} />
      </Row>
    );
  };

  const openPricing = (): void => {
    openWebsitePricing({
      workspaceId,
      isQuickSettingsEnabled,
    });
  };

  const renderTotal = (amount: number, payment: IPayment): JSX.Element => {
    const isLastPayment = payments.findIndex(elem => elem.id === payment.id) === 0;
    if (isLastPayment && payment.status !== 'success') {
      return (
        <ButtonPay onClick={openPricing} type='primary'>
          {translation('personalArea.billing.paySubmitBtnText')}
        </ButtonPay>
      );
    }

    const { currency = E_CURRENCY.USD, additionalParams = {}, isTrial } = payment;
    const { amountInCurrency = 0 } = additionalParams;
    const displayedCurrency = isTrial ? localCurrency : currency;
    const formattedSum = new Price(amountInCurrency, displayedCurrency).toLocalString(0);

    return (
      <TextTable style={{ marginLeft: 20 }}>
        {formattedSum}
      </TextTable>
    );
  };

  const handleMouseEnter = (event: React.MouseEvent<HTMLDivElement>, translationKey: string, tooltipPlace: TooltipPlacement = 'top'): void => {
    setAnchorElTooltip(event.currentTarget);
    setTooltipPlacement(tooltipPlace);
    setTooltipText(translation(translationKey));
  };

  const makeInvoice = async (paymentId: string) => {
    const { url } = await submitCreateInvoice({ paymentId });
    openUrl(url);
  };

  const renderInvoice = (additionalParams: IAdditionalParams, payment: IPayment): JSX.Element | null => {
    const { id: paymentId, amount, currency } = payment;
    if (!amount || currency === E_CURRENCY.RUB) {
      return null;
    }

    return (
      <TextTable>
        <ReceiptIconContainerWithDescription
          onClick={() => makeInvoice(paymentId)}
          onMouseEnter={(event): void => handleMouseEnter(event, 'personalArea.billing.invoiceGetTooltipText')}
          onMouseLeave={(): void => setAnchorElTooltip(null)}
        >
          <IconReceipt />
        </ReceiptIconContainerWithDescription>
        <TooltipCustom style={{ marginBottom: '10px' }} anchorEl={anchorElTooltip} value={tooltipText || ''} placement={tooltipPlacement} />
      </TextTable>
    );
  };

  return (
    <TablePan
      pagination={false}
      dataSource={payments}
      scroll={{ x: true }}
    >
      <Table.Column
        ellipsis={true}
        width={140}
        title={(
          <ContainerNameCol>
            <span>
              {translation('personalArea.billing.dateColumn')}
            </span>
          </ContainerNameCol>
        )}
        dataIndex='createdAt'
        key='createdAt'
        render={renderDate}
      />
      <Table.Column
        ellipsis={true}
        width={140}
        title={(
          <ContainerNameCol>
            <span>
              {translation('personalArea.billing.expiresColumn')}
            </span>
          </ContainerNameCol>
        )}
        dataIndex='expiresIn'
        key='expiresIn'
        render={renderExpiresDate}
      />
      <Table.Column
        ellipsis={true}
        width={140}
        title={(
          <ContainerNameCol>
            <span>
              {translation('personalArea.billing.paymentMethodColumn')}
            </span>
          </ContainerNameCol>
        )}
        dataIndex='description'
        key='description'
        render={renderPaymentMethod}
      />
      <Table.Column
        ellipsis={true}
        title={(
          <ContainerNameCol>
            <span>
              {translation('personalArea.billing.statusColumn')}
            </span>
          </ContainerNameCol>
        )}
        width={50}
        dataIndex='status'
        key='status'
        render={renderStatus}
      />
      <Table.Column
        ellipsis={true}
        title={(
          <ContainerNameCol style={{ marginLeft: 16 }}>
            <span>
              {translation('personalArea.billing.totalColumn')}
            </span>
          </ContainerNameCol>
        )}
        width={50}
        dataIndex='amount'
        key='amount'
        render={renderTotal}
      />
      <Table.Column
        ellipsis={true}
        width={0}
        dataIndex='additionalParams'
        key='invoice'
        render={renderInvoice}
      />
    </TablePan>
  );
};

export default React.memo(PaymentsTable);
